import { PortfolioOrder, PortfolioResponse } from "../../types/portfolio";

export const numberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const markFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const entryFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const entryPriceFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const quantityFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const datetimeFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
  numeric: "auto",
});

const relativeTimeUnits: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: "year", ms: 1000 * 60 * 60 * 24 * 365 },
  { unit: "month", ms: 1000 * 60 * 60 * 24 * 30 },
  { unit: "week", ms: 1000 * 60 * 60 * 24 * 7 },
  { unit: "day", ms: 1000 * 60 * 60 * 24 },
  { unit: "hour", ms: 1000 * 60 * 60 },
  { unit: "minute", ms: 1000 * 60 },
  { unit: "second", ms: 1000 },
];

export function formatRelativeTime(dateValue: string | Date) {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const diffMs = date.getTime() - Date.now();
  for (const { unit, ms } of relativeTimeUnits) {
    const value = diffMs / ms;
    if (Math.abs(value) >= 1 || unit === "second") {
      return relativeTimeFormatter.format(Math.round(value), unit);
    }
  }

  return relativeTimeFormatter.format(0, "second");
}

/**
 * Get display entry price (absolute value of avg_cost).
 */
export function getEntryPrice(
  position: { avg_cost: number | null; current_score: number }
): number {
  return position.avg_cost != null ? Math.abs(position.avg_cost) : position.current_score;
}

export function computeMetrics(portfolio: PortfolioResponse | null) {
  if (!portfolio) {
    return {
      deltaWithLeader: 0,
    };
  }

  // Calculate delta with leader based on points balance
  const userPoints = portfolio.standing.points_balance ?? 0;
  const leaderPoints = portfolio.standing.leader_points_balance ?? 0;
  const deltaWithLeader = userPoints - leaderPoints;

  return {
    deltaWithLeader,
  };
}

export function groupOrders(orders: PortfolioOrder[]) {
  const openOrders = orders.filter(
    (order) => order.status === "OPEN" && order.open_quantity > 0.0001,
  );
  const filledOrders = orders.filter((order) => order.status === "FILLED");
  const cancelledOrders = orders.filter((order) => order.status === "CANCELLED");
  return {
    openOrders,
    filledOrders,
    cancelledOrders,
  };
}

export function cancelledQuantity(order: PortfolioOrder): number {
  const remaining = order.original_quantity - order.filled_quantity;
  return remaining > 0.0001 ? remaining : 0;
}
